/**
 * Consultations (admin) — the host-side management list.
 *
 * A staff/admin surface (team-auth-gated at the route) listing upcoming/active
 * consultations from the team-only `consultations` collection, with per-row
 * **Reschedule** and **Cancel** controls that call Mack's team-auth callables
 * through a clean seam (`consultAdminApi`). The callables aren't deployed yet,
 * so the actions degrade honestly (loading → error) — never a fake success.
 *
 * AVBP green + system-font tokens (consultAdmin.css). One page scroll (the list
 * grows with content; the only scroll container is a dialog, when open). No dead
 * controls — action buttons appear only on actionable statuses.
 *
 * `?preview=1` (and the standalone harness) inject sample data + the mock seam
 * so QA can walk every state with no backend/auth.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  listenConsultations,
  type AdminConsultation,
  type AdminConsultSource,
  type AdminConsultStatus,
  type ConsultationsFeed,
} from '../lib/consultAdminStore';
import {
  liveConsultAdminApi,
  type ConsultAdminApi,
} from '../lib/consultAdminApi';
import { describeSlotView, shortZoneName } from '../lib/publicSlots';
import { IconExternal, IconMeet } from '../doorb/icons';
import RescheduleConsultDialog from './RescheduleConsultDialog';
import CancelConsultDialog from './CancelConsultDialog';
import '../styles/consultAdmin.css';

const STATUS_LABEL: Record<AdminConsultStatus, string> = {
  pending: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  no_show: 'No-show',
  done: 'Completed',
  unknown: 'Booked',
};
const STATUS_CLASS: Record<AdminConsultStatus, string> = {
  pending: 'ca-chip-pending',
  confirmed: 'ca-chip-confirmed',
  rescheduled: 'ca-chip-rescheduled',
  cancelled: 'ca-chip-cancelled',
  no_show: 'ca-chip-cancelled',
  done: 'ca-chip-confirmed',
  unknown: '',
};
const SOURCE_LABEL: Record<AdminConsultSource, string> = {
  door_a_staff: 'Booked by team',
  door_b_selfserve: 'Self-serve link',
  unknown: 'Source unknown',
};

/** Grace so a call already in progress still counts as "not past". */
const PAST_GRACE_MS = 3 * 60 * 60 * 1000;

/** A live status that could be rescheduled/cancelled (before the time check). */
function isLiveStatus(s: AdminConsultStatus): boolean {
  return s === 'pending' || s === 'confirmed';
}

/** Not in the past (with grace). A missing start is treated as not-past (the
 *  time is still to be confirmed). */
function notInPast(startISO: string | null, cutoff: number): boolean {
  if (!startISO) return true;
  return Date.parse(startISO) >= cutoff;
}

/** Reschedule/Cancel only on a live status whose time hasn't passed — a past or
 *  cancelled meeting isn't reschedulable/cancellable from here (no dead controls). */
function isActionable(c: AdminConsultation, cutoff: number): boolean {
  return isLiveStatus(c.status) && notInPast(c.startISO, cutoff);
}

function istLabel(c: AdminConsultation): string {
  if (!c.startISO || !c.endISO) return 'Time to be confirmed';
  const v = describeSlotView(c.startISO, c.endISO, c.timeZone || 'Asia/Kolkata');
  return `${v.dayFull} · ${v.range} (IST)`;
}
function clientTzLabel(c: AdminConsultation): string | null {
  if (!c.startISO || !c.endISO || !c.clientTz) return null;
  if (c.clientTz === (c.timeZone || 'Asia/Kolkata')) return null;
  const v = describeSlotView(c.startISO, c.endISO, c.clientTz);
  return `${v.range} for the client (${shortZoneName(c.clientTz)})`;
}

export interface ConsultationsAdminProps {
  /** Preview/QA: swap the live callables for the mock seam. */
  apiOverride?: ConsultAdminApi;
  /** Preview/QA: render this sample list instead of the live listener. */
  sampleFeed?: AdminConsultation[];
}

export default function ConsultationsAdmin({
  apiOverride,
  sampleFeed,
}: ConsultationsAdminProps) {
  const api = apiOverride ?? liveConsultAdminApi;
  const preview = Boolean(sampleFeed);

  const [feed, setFeed] = useState<ConsultationsFeed>(
    sampleFeed
      ? { phase: 'ready', items: sampleFeed }
      : { phase: 'loading', items: [] },
  );
  const [filter, setFilter] = useState<'upcoming' | 'all'>('upcoming');
  const [dialog, setDialog] = useState<
    | { kind: 'reschedule'; consult: AdminConsultation }
    | { kind: 'cancel'; consult: AdminConsultation }
    | null
  >(null);
  const liveRef = useRef<HTMLDivElement | null>(null);
  const announce = (msg: string) => {
    if (liveRef.current) liveRef.current.textContent = msg;
  };

  useEffect(() => {
    if (sampleFeed) return; // preview — no live subscription
    return listenConsultations(setFeed);
  }, [sampleFeed]);

  // One cutoff per render, shared by the Upcoming filter AND the per-row action
  // gate, so a past confirmed/pending row (only visible under "All") shows no
  // Reschedule/Cancel — the actions can't act on a past meeting.
  const cutoff = Date.now() - PAST_GRACE_MS;
  const rows = useMemo(() => {
    if (filter === 'all') return feed.items;
    return feed.items.filter((c) => isActionable(c, cutoff));
  }, [feed.items, filter, cutoff]);

  // Optimistic local patch so preview reflects an action immediately (live mode
  // gets the same result from the onSnapshot stream).
  const patch = (id: string, next: Partial<AdminConsultation>) =>
    setFeed((f) => ({ ...f, items: f.items.map((c) => (c.id === id ? { ...c, ...next } : c)) }));

  return (
    <div className="ca-root">
      <div className="ca-sr" role="status" aria-live="polite" ref={liveRef} />

      <div className="ca-head">
        <div>
          <h1 className="ca-h1">Consultations</h1>
          <p className="ca-sub">
            Upcoming discovery calls — reschedule or cancel on the client&apos;s behalf.
          </p>
        </div>
        <div className="ca-filter" role="group" aria-label="Filter consultations">
          <button
            type="button"
            aria-pressed={filter === 'upcoming'}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming
          </button>
          <button
            type="button"
            aria-pressed={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>
      </div>

      {preview && (
        <div className="ca-preview-ribbon" role="note">
          Preview — sample data, nothing is saved
        </div>
      )}

      {feed.phase === 'loading' && (
        <div className="ca-state" role="status" aria-live="polite">
          <span className="ca-spinner" aria-hidden="true" />
          Loading consultations…
        </div>
      )}

      {feed.phase === 'error' && rows.length === 0 && (
        <div className="ca-state ca-state-err" role="alert">
          We couldn&apos;t load consultations just now. Please refresh in a moment.
        </div>
      )}

      {feed.phase === 'ready' && rows.length === 0 && (
        <div className="ca-state" role="status">
          {filter === 'upcoming'
            ? 'No upcoming consultations. New bookings from the booking link appear here automatically.'
            : 'No consultations yet.'}
        </div>
      )}

      {rows.length > 0 && (
        <ul className="ca-list">
          {rows.map((c) => {
            const tzLine = clientTzLabel(c);
            return (
              <li key={c.id} className="ca-card">
                <div className="ca-card-top">
                  <div>
                    <h2 className="ca-name">{c.clientName}</h2>
                    <p className="ca-contact">
                      {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact details'}
                    </p>
                  </div>
                  <div className="ca-chips">
                    <span className={`ca-chip ${STATUS_CLASS[c.status]}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                    <span className="ca-chip ca-chip-source">{SOURCE_LABEL[c.source]}</span>
                  </div>
                </div>

                <div className="ca-when">
                  <span className="ca-when-ist">{istLabel(c)}</span>
                  {tzLine && <span className="ca-when-tz">{tzLine}</span>}
                  {c.typeName && <span className="ca-when-tz">{c.typeName}</span>}
                </div>

                {c.meetUrl ? (
                  <a
                    className="ca-meet"
                    href={c.meetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Join Google Meet (opens in a new tab)"
                  >
                    <IconMeet size={16} /> {c.meetUrl.replace(/^https?:\/\//, '')}
                    <IconExternal size={14} />
                  </a>
                ) : (
                  <span className="ca-meet-none">
                    {c.status === 'pending'
                      ? 'Meet link pending confirmation'
                      : 'No Meet link'}
                  </span>
                )}

                {isActionable(c, cutoff) && (
                  <div className="ca-actions">
                    <button
                      type="button"
                      className="ca-btn ca-btn-line"
                      onClick={() => setDialog({ kind: 'reschedule', consult: c })}
                    >
                      Reschedule
                    </button>
                    <button
                      type="button"
                      className="ca-btn ca-btn-danger"
                      onClick={() => setDialog({ kind: 'cancel', consult: c })}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {dialog?.kind === 'reschedule' && (
        <RescheduleConsultDialog
          consult={dialog.consult}
          api={api}
          whenLabel={istLabel(dialog.consult)}
          announce={announce}
          onClose={() => setDialog(null)}
          onRescheduled={(startISO, endISO) => {
            patch(dialog.consult.id, { startISO, endISO, status: 'confirmed' });
            setDialog(null);
          }}
        />
      )}

      {dialog?.kind === 'cancel' && (
        <CancelConsultDialog
          consult={dialog.consult}
          api={api}
          whenLabel={istLabel(dialog.consult)}
          announce={announce}
          onClose={() => setDialog(null)}
          onCancelled={() => {
            patch(dialog.consult.id, { status: 'cancelled', meetUrl: null });
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}
